import { useState, useCallback } from "react";
import { useAdminWrap } from "~/hooks/useAdminKeyManagement";

export interface ResetUserKeyTarget {
    userId: string;
    userName: string;
}

/**
 * Hook that manages the state for the ResetUserKeyDialog.
 * Centralises the open/close logic so any component can trigger
 * the same reset flow without duplicating state.
 *
 * @param enabled - When false, skips the admin-wrap fetch (e.g. standalone users).
 */
export function useResetUserKey(enabled = true) {
    const [dialog, setDialog] = useState<{ open: boolean; userId: string; userName: string }>({
        open: false,
        userId: "",
        userName: "",
    });

    const { data: adminWrap } = useAdminWrap(enabled);
    const canResetUserKey = !!adminWrap && !adminWrap.must_change_password;

    const openResetDialog = useCallback((target: ResetUserKeyTarget) => {
        setDialog({ open: true, userId: target.userId, userName: target.userName });
    }, []);

    const closeResetDialog = useCallback(() => {
        setDialog((s) => ({ ...s, open: false }));
    }, []);

    const onOpenChange = useCallback((open: boolean) => {
        setDialog((s) => ({ ...s, open }));
    }, []);

    return {
        resetDialog: dialog,
        adminWrap: adminWrap ?? null,
        canResetUserKey,
        openResetDialog,
        closeResetDialog,
        onOpenChange,
    };
}
